import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Box,
  useTheme,
  Container,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const FAQPage = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const theme = useTheme();

  const handleAccordionChange = (index) => (_, isExpanded) => {
    setExpandedIndex(isExpanded ? index : null);
  };

  let faqCounter = 0;

  return (
    <Container maxWidth="lg">
      <Box
        sx={{
          width: "100%",
          mt: { xs: 8, md: 12 },
          mb: { xs: 6, md: 10 },
          boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
          borderRadius: 4,
          overflow: "hidden",
          backgroundColor: theme.palette.background.paper,
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Grid container spacing={{ xs: 3, md: 5 }} alignItems="flex-start">
          {/* Image */}
          <Grid item xs={12} md={5}>
            <Box
              component="img"
              src="https://ik.imagekit.io/arthgyan/Arthgyan_Web/faq"
              alt="FAQ"
              sx={{
                width: "100%",
                maxWidth: { xs: "280px", sm: "350px", md: "100%" },
                height: "auto",
                borderRadius: 3,
                position: "sticky",
                top: 100,
              }}
            />
          </Grid>

          {/* FAQ Section */}
          <Grid item xs={12} md={7}>
            <Typography
              variant="h4"
              align="center"
              sx={{
                fontSize: { xs: "22px", sm: "26px", md: "32px" },
                fontWeight: 700,
                mb: 4,
                color: theme.palette.primary.main,
              }}
            >
              Frequently Asked Questions
            </Typography>

            {faqSections.map((section, sectionIndex) => (
              <Box key={sectionIndex} sx={{ mb: 4 }}>
                {/* Section Heading */}
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    mb: 1,
                  }}
                >
                  {section.title}
                </Typography>

                <Divider sx={{ mb: 2 }} />

                {/* Questions */}
                {section.questions.map((faq, index) => {
                  const currentIndex = faqCounter++;
                  const isExpanded = expandedIndex === currentIndex;

                  return (
                    <Accordion
                      key={currentIndex}
                      expanded={isExpanded}
                      onChange={handleAccordionChange(currentIndex)}
                      sx={{
                        mb: 1.5,
                        borderRadius: 2,
                        overflow: "hidden",
                        backgroundColor: theme.palette.secondary.main,
                        "&:before": { display: "none" },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon
                            sx={{
                              color: theme.palette.primary.main,
                              transform: isExpanded
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "0.3s",
                            }}
                          />
                        }
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            fontSize: {
                              xs: "14px",
                              sm: "15px",
                              md: "16px",
                            },
                            color: theme.palette.text.primary,
                          }}
                        >
                          {faq.question}
                        </Typography>
                      </AccordionSummary>

                      <AccordionDetails
                        sx={{
                          backgroundColor: "#00a8a8",
                          color: "#fff",
                          px: { xs: 2, md: 3 },
                          py: 2,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: {
                              xs: "13px",
                              sm: "14px",
                              md: "15px",
                            },
                            lineHeight: 1.6,
                          }}
                        >
                          {faq.answer}
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
              </Box>
            ))}
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};



const faqSections = [
  {
    title: "1. Getting Started",
    questions: [
      {
        question: "What is ArthGyan?",
        answer:
          "ArthGyan is a community focused and community first financial platform that helps government employees make better financial decisions—whether it’s investment, banking products, insurance, or financial planning.",
      },
      {
        question: "Who can use ArthGyan?",
        answer:
          "Government employees (Central & State), PSU employees, and anyone looking for simple financial guidance (basic access).",
      },
      {
        question: "Is ArthGyan free to use?",
        answer: "Yes, ArthGyan is completely free for users.",
      },
      {
        question: "How do I get started?",
        answer:
          "Just sign up on the app, explore content, and choose the services you need.",
      },
    ],
  },

  {
    title: "2. Services on ArthGyan",
    questions: [
      {
        question: "What can I do on ArthGyan?",
        answer:
          "You can buy investment products like mutual funds and invest in FDs, explore insurance plans, apply for loans, and get guidance on financial decisions.",
      },
      {
        question: "Does ArthGyan provide financial advice?",
        answer:
          "ArthGyan provides easy-to-understand guidance to help you make informed decisions.",
      },
    ],
  },

  {
    title: "3. Insurance",
    questions: [
      {
        question: "What types of insurance can I get?",
        answer:
          "Life Insurance (Term Plans), Health Insurance, and Motor Insurance.",
      },
      {
        question:
          "Why do I need insurance if I already have government benefits?",
        answer:
          "Government benefits may not be enough for rising medical costs and family protection. Insurance gives extra financial security.",
      },
      {
        question:
          "How does ArthGyan help me choose the right policy?",
        answer:
          "It explains policies in simple terms, helps compare options, and recommends what suits your needs.",
      },
    ],
  },

  {
    title: "4. Loans",
    questions: [
      {
        question: "What types of loans are available?",
        answer: "Personal Loan, Home Loan, and Car Loan.",
      },
      {
        question:
          "Will I get better loan offers as a government employee?",
        answer:
          "Yes, government employees often get lower interest rates and higher approval chances.",
      },
      {
        question: "Does ArthGyan charge anything for loans?",
        answer: "No, you don’t pay anything to use ArthGyan.",
      },
    ],
  },

  {
    title: "5. Trust & Safety",
    questions: [
      {
        question: "Is ArthGyan safe to use?",
        answer: "Yes, your data is secure and protected.",
      },
      {
        question: "Will my information be shared?",
        answer:
          "Your data is only shared with partners when needed to provide a service.",
      },
      {
        question: "Will I get spam calls?",
        answer:
          "No. ArthGyan ensures limited and relevant communication only.",
      },
    ],
  },

  {
    title: "6. Using the App",
    questions: [
      {
        question: "Can I compare different options?",
        answer:
          "Yes, you can explore and compare products before making a decision.",
      },
      {
        question: "Can I use ArthGyan anytime?",
        answer: "Yes, the app is available 24/7.",
      },
    ],
  },

  {
    title: "7. Support",
    questions: [
      {
        question: "What if I need help?",
        answer:
          "You can contact support through in-app help, email, WhatsApp, or call (8AM–8PM).",
      },
      {
        question: "Will someone guide me personally?",
        answer:
          "Yes, support is available to help you understand and choose better.",
      },
    ],
  },

  {
    title: "8. Common Questions",
    questions: [
      {
        question: "Are there any hidden charges?",
        answer: "No, there are no hidden costs.",
      },
      {
        question: "Do I have to buy anything?",
        answer:
          "No, you can simply learn and explore without buying.",
      },
      {
        question: "Why should I use ArthGyan?",
        answer:
          "Because it helps you avoid wrong financial decisions, understand money easily, and choose the right products with confidence.",
      },
    ],
  },
];

export default FAQPage;